import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { RichTreeView, TreeItem2 } from '@mui/x-tree-view';
import { CheckboxUI } from '../Interface';
import { Typography } from '@mui/material';
import { sxConfig } from '../ProductionGeneratorPartial/ProductionGeneratorPartial.style';

const ProductionGeneratorPartialVariantTree = ({
  sourceDataState,
  treeDataState,
  expandedItemsState,
  selectedItemState,
  removedItemState,
  selectedOrderState,
  newProdsState,
  prodsAndFromWhsState,
}) => {
  const { sourceData } = sourceDataState;
  const { treeData } = treeDataState;
  const { expandedItems, setExpandedItems } = expandedItemsState;
  const { setSelectedItem } = selectedItemState;
  const { setRemovedItem } = removedItemState;
  const { selectedOrder } = selectedOrderState;
  const { newProds } = newProdsState;
  const { prodsAndFromWhs } = prodsAndFromWhsState;

  const [checkedState, setCheckedState] = useState([]);

  const generateKey = (findedItem) => {
    if (!findedItem) return '';
    return `${findedItem.variantMainItemCode ?? findedItem.rootItemCode}_${findedItem.level === 1 ? findedItem.relatedItemCode : findedItem.parentItemCode}_${findedItem.relatedItemCode}_${findedItem.level}`;
  };

  const generateKeyFromProd = (prodItems) => {
    return prodItems.map(
      (prodItem) =>
        `${prodItem.finishedItemCode}_${prodItem.variantFinishedItemCode}_${prodItem.variantItemCode}_${prodItem.level}`
    );
  };

  const generateVariantGroupKey = (item) => {
    if (!item) return '';

    const parentItemCode = item.level === 1 ? item.relatedItemCode : item.parentItemCode;
    const variantBaseItemCode = item.sourceList?.variantMainItemCode ?? item.relatedItemCode;

    return `${item.rootItemCode}_${parentItemCode}_${item.level}_${variantBaseItemCode}`;
  };

  const findTreeItemById = (items, itemId) => {
    for (const item of items) {
      if (item.id === itemId) return item;
      const child = item.children ? findTreeItemById(item.children, itemId) : null;
      if (child) return child;
    }
    return null;
  };

  useEffect(() => {
    if (!sourceData.length) return;
    const filteredProds = newProds.filter(
      (prod) =>
        prod.baseDocType === selectedOrder.docTypeId &&
        prod.baseId === selectedOrder.docId &&
        prod.baseLine === selectedOrder.lineNum
    );

    const inNewprodTable = generateKeyFromProd(filteredProds);

    setCheckedState([...inNewprodTable]);
  }, [sourceData]);

  const handleExpandedItemsChange = (event, itemIds) => {
    setExpandedItems(itemIds);
  };

  const getItemLabel = (item) => {
    return `${item.relatedItemCode} - ${item.relatedItemName} ${item.variantNote ? `|| ${item.variantNote}` : ''}`;
  };

  const addSelection = (findedItem) => {
    const key = generateKey(findedItem);
    setCheckedState((prev) => [...prev, key]);
    setRemovedItem(null);
    setSelectedItem(findedItem);
  };

  const removeSelection = (findedItem) => {
    const key = generateKey(findedItem);
    setCheckedState((prev) => prev.filter((k) => k !== key));
    setRemovedItem(findedItem);
  };

  const computeLabelColor = ({ checked, hasWarehouse, hasProduction, hasOps, variantLocked }) => {
    if (checked) return 'success.main';
    if (hasWarehouse) return 'warning.main';
    if (hasProduction) return 'warning.main';
    if (variantLocked) return 'text.disabled';
    if (!hasOps) return 'red';
    return '';
  };

  const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
    const findedItem = findTreeItemById(treeData, props.itemId);
    const key = generateKey(findedItem);

    const findedItemSourceList = findedItem?.sourceList ?? {};
    const findedItemSourceListOp = findedItemSourceList?.sourceListOperation ?? [];

    const checked = checkedState.includes(key);

    const finishedItemCode = findedItem?.variantMainItemCode ?? findedItem?.rootItemCode;

    const hasProduction = prodsAndFromWhs.production.some(
      (prod) =>
        prod.articleNumber === findedItem?.relatedvariantItemCode &&
        prod.level === findedItem?.level &&
        prod.finishedItemCode === finishedItemCode &&
        prod.variantItemCode === findedItem.relatedItemCode &&
        prod.variantFinishedItemCode ===
          (findedItem.level === 1 ? findedItem.relatedItemCode : findedItem.parentItemCode)
    );

    const hasWarehouse = prodsAndFromWhs.fromWarehouse.some(
      (fromWhs) =>
        fromWhs.itemCode === findedItem?.relatedvariantItemCode &&
        fromWhs.variantItemCode === findedItem.relatedItemCode &&
        fromWhs.mainItemCode === (findedItem.level === 1 ? findedItem.relatedItemCode : findedItem.parentItemCode)
    );

    const variantGroupKey = generateVariantGroupKey(findedItem);
    const anyOtherCheckedInVariantGroup =
      !!findedItem &&
      checkedState.some((checkedKey) => {
        if (checkedKey === key) return false;

        const checkedItem = sourceData.find((item) => generateKey(item) === checkedKey);
        return generateVariantGroupKey(checkedItem) === variantGroupKey;
      });

    const variantLocked = anyOtherCheckedInVariantGroup && !checked;

    const disabled = hasProduction || hasWarehouse || variantLocked;

    const hasOps = Object.keys(findedItemSourceList).length > 0 && findedItemSourceListOp.length > 0;
    const labelColor = computeLabelColor({ checked, hasWarehouse, hasProduction, hasOps, variantLocked });

    return (
      <TreeItem2
        {...props}
        ref={ref}
        disabled={disabled}
        label={
          <Box sx={{ color: labelColor, ...sxConfig.checkBoxMainBox }}>
            <CheckboxUI
              onClick={(event) => event.stopPropagation()}
              checked={checked}
              disabled={disabled}
              size="small"
              onChange={(e) => {
                if (!findedItem || disabled) return;
                if (e.target.checked) addSelection(findedItem);
                else removeSelection(findedItem);
              }}
              sx={sxConfig.checkBox}
            />
            <Typography>{props.label}</Typography>
          </Box>
        }
      />
    );
  });

  return (
    <Box sx={sxConfig.richTreeView}>
      <RichTreeView
        items={treeData}
        getItemLabel={getItemLabel}
        expandedItems={expandedItems}
        onExpandedItemsChange={handleExpandedItemsChange}
        slots={{
          item: CustomTreeItem,
        }}
      />
    </Box>
  );
};

export default ProductionGeneratorPartialVariantTree;
